import { useEffect } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export default function useSmoothScroll() {
  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const lenis = reduce ? null : new Lenis({
      duration: 1.15,
      easing: (t: number) => Math.min(1, 1.001 - 2 ** (-10 * t)),
      smoothWheel: true,
    });

    const tick = (time: number) => { lenis?.raf(time * 1000); };
    if (lenis) {
      lenis.on('scroll', ScrollTrigger.update);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);
    }

    const onClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest('a');
      const href = link?.getAttribute('href');
      if (!href || !href.startsWith('#')) return;
      e.preventDefault();
      const target = href === '#' || href === '#top' ? 0 : document.querySelector<HTMLElement>(href);
      if (target === null) return;
      if (lenis) {
        lenis.scrollTo(target, { duration: 1.6, offset: typeof target === 'number' ? 0 : -64 });
      } else {
        const y = typeof target === 'number' ? 0 : target.getBoundingClientRect().top + window.scrollY - 64;
        window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
      }
      window.history.replaceState(null, '', href === '#' ? window.location.pathname : href);
    };
    document.addEventListener('click', onClick);

    // Recalculate trigger positions when page height changes (late images/fonts,
    // editor frames resizing) so reveals fire at the right scroll position.
    let rt = 0;
    const refresh = () => { window.clearTimeout(rt); rt = window.setTimeout(() => ScrollTrigger.refresh(), 200); };
    const ro = new ResizeObserver(refresh);
    ro.observe(document.body);
    window.addEventListener('load', refresh);

    return () => {
      document.removeEventListener('click', onClick);
      ro.disconnect();
      window.removeEventListener('load', refresh);
      window.clearTimeout(rt);
      if (lenis) {
        gsap.ticker.remove(tick);
        lenis.destroy();
      }
    };
  }, []);
}